import { Tooltip } from "@mui/material";
import { SettingsOutlined } from "@mui/icons-material";
import { ColDef, IHeaderParams } from "ag-grid-community";
import { IHeaderReactComp } from "ag-grid-react";
import ArrowPopper from "components/app/ArrowPopper";
import {
  BREAK_UP_AOV_CLOUD_CONTACT_CENTER_COLUMN,
  BREAK_UP_CLOUD_CONTACT_CENTER_COLUMN,
} from "constant";
import {
  forwardRef,
  MouseEvent,
  TouchEvent,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import CollectionRevenueFilter from "../customerFilters/CollectionRevenueFilter";
import { CollectionRevenueContext } from "./CollectionRevenueCellRenderer";
import { classHOC } from "components/app/ClassWrapper";

const GridHeader = forwardRef<
  IHeaderReactComp & { refresh: () => boolean },
  IHeaderParams & {
    collection: string[];
    headerTooltip?: any;
    type?: string;
    stateKey?: string;
  }
>(
  (
    {
      enableMenu,
      enableSorting,
      showColumnMenu,
      column,
      setSort,
      displayName,
      api,
      collection,
      headerTooltip,
      type = "arr",
      stateKey,
    },
    ref
  ) => {
    useImperativeHandle(ref, () => ({
      refresh: () => false,
    }));

    const { collectionColumns, aovCollectionColumns } = useContext(CollectionRevenueContext);

    const [enableFilter, setEnableFilter] = useState(false);

    const columns = useMemo(() => {
      return (type === "arr" ? collectionColumns : aovCollectionColumns)!
        .sort((a, b) => collection.findIndex((x) => x === a) - collection.findIndex((x) => x === b))
        .map((x) => api.getColumnDef(x));
    }, [collectionColumns, aovCollectionColumns]);

    const getCurrentSortOrder = () =>
      column.isSortAscending() ? "asc" : column.isSortDescending() ? "desc" : "none";

    const getNextSortOrder = () =>
      column.isSortAscending() ? "none" : column.isSortDescending() ? "asc" : "desc";

    const [sortState, setSortState] = useState<"asc" | "desc" | "none">(getCurrentSortOrder());
    const refButton = useRef<HTMLDivElement>(null);

    const [showMenu, setShowMenu] = useState(false);

    const [anchorEl, setAnchorEl] = useState(null);
    const popRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
      document.addEventListener("click", closePop);
      return () => {
        document.removeEventListener("click", closePop);
      };
    }, []);

    const closePop = (e: Event) => {
      if (
        !popRef.current?.contains(e.target as Node) &&
        popRef.current !== e.target &&
        !btnRef.current?.contains(e.target as Node)
      ) {
        setAnchorEl(null);
      }
    };

    const onMenuClicked = () => {
      refButton.current ? showColumnMenu(refButton.current) : null;
    };

    const onSortChanged = () => {
      setSortState(getCurrentSortOrder());
    };

    const onSortRequested = (
      order: string,
      event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
    ) => {
      setSort(order, event.shiftKey);
    };

    const onFilterChanged = () => {
      const filterModel = api?.getFilterModel();
      if (type === "arr") {
        const { invoiceArrCustom, ..._other } = filterModel;
        if (invoiceArrCustom) {
          setEnableFilter(true);
        } else {
          setEnableFilter(false);
        }
      } else {
        const { aovCustom, ..._other } = filterModel;
        if (aovCustom) {
          setEnableFilter(true);
        } else {
          setEnableFilter(false);
        }
      }
    };

    const showColumnSelect = (event: any) => {
      if (anchorEl) {
        setAnchorEl(null);
      } else {
        setAnchorEl(event.currentTarget);
      }
      event.stopPropagation();
    };

    useEffect(() => {
      column.addEventListener("sortChanged", onSortChanged);
      onSortChanged();
    }, []);

    useEffect(() => {
      column.addEventListener("filterChanged", onFilterChanged);
      onFilterChanged();
    }, []);

    const handleBreakupCols = (data: any) => {
      if (
        data.filter((col: ColDef) =>
          (type === "arr"
            ? BREAK_UP_CLOUD_CONTACT_CENTER_COLUMN
            : BREAK_UP_AOV_CLOUD_CONTACT_CENTER_COLUMN
          ).includes(col?.colId as string)
        )?.length
      ) {
        return [
          ...data,
          api.getColumnDef(
            type === "arr" ? "invoiceArrContactCenterCloud" : "aovContactCenterCloud"
          ),
        ];
      }
      return data;
    };
    const subText =
      columns.length === collection.length
        ? ["All Selected"]
        : handleBreakupCols(columns).map((x: ColDef) =>
            x?.headerName?.split(/\((.*)\)/)?.[1] === "Last 3 Years"
              ? "Total Device Bookings $$ (Last 3 Years)"
              : x?.headerName?.split(/\((.*)\)/)?.[1]
          );
    return (
      <div
        style={{ display: "flex" }}
        onMouseEnter={() => setShowMenu(true)}
        onMouseLeave={() => setShowMenu(false)}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            // marginTop: 10,
            width: "75%",
            cursor: enableSorting ? "pointer" : "default",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              // // flexDirection: "column",
              width: "100%",
              flexShrink: 1,
              // overflow: "hidden",
            }}
            onClick={(event) =>
              enableSorting ? onSortRequested(getNextSortOrder(), event) : undefined
            }
            onTouchEnd={(event) =>
              enableSorting ? onSortRequested(getNextSortOrder(), event) : undefined
            }
          >
            <SettingsOutlined
              onClick={showColumnSelect}
              // fontSize="small"
              style={{ marginRight: 5 }}
              ref={btnRef}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Tooltip
                title={<div dangerouslySetInnerHTML={{ __html: headerTooltip }}></div>}
                placement="top"
              >
                <span className="ag-header-cell-text">{displayName}</span>
              </Tooltip>

              {subText.length ? (
                <Tooltip
                  title={
                    <ul>
                      {subText
                        .filter((f: string) => f !== "Cloud Contact Center")
                        .map((x: string) => (
                          <li key={x}>{x}</li>
                        ))}
                    </ul>
                  }
                >
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: "#00A0D1",
                      fontSize: 9,
                      marginTop: -2,
                      // width: "100%",
                      // maxWidth: 200,
                    }}
                  >
                    ({subText.filter((f: string) => f !== "Cloud Contact Center")?.join(" + ")})
                  </span>
                </Tooltip>
              ) : null}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexShrink: 0 }}>
          {enableFilter && (
            <div>
              <span className="ag-header-icon ag-filter-icon">
                <span className={`ag-icon ag-icon-filter`}></span>
              </span>
            </div>
          )}
          {enableSorting && (
            <div
              style={{ display: "inline-block" }}
              onClick={(event) => onSortRequested(getNextSortOrder(), event)}
              onTouchEnd={(event) => onSortRequested(getNextSortOrder(), event)}
            >
              <div>
                <span className={`ag-icon ag-icon-${sortState}`}></span>
              </div>
            </div>
          )}
          {enableMenu && (
            <div
              ref={refButton}
              onClick={() => onMenuClicked()}
              style={{
                opacity: showMenu ? 1 : 0,
                transition: "opacity 0.2s ease 0s, border 0.2s ease 0s",
              }}
            >
              <span className={`ag-icon ag-icon-menu`}></span>
            </div>
          )}
        </div>
        <ArrowPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="bottom"
          disablePortal={false}
          containerStyle={{ padding: 0 }}
        >
          <div ref={popRef}>
            <CollectionRevenueFilter
              collection={collection}
              api={api}
              column={column}
              type={type}
              stateKey={stateKey}
            />
          </div>
        </ArrowPopper>
      </div>
    );
  }
);

export default classHOC(GridHeader);
